'use client';

import Link from 'next/link';
import { RefreshCw, XCircle } from 'lucide-react';
import { useBasePath } from '../navigation/base-path';
import { InfiniteScrollTrigger } from './infinite-scroll-trigger';
import { S3Image } from './s3-image';
import { VideoThumbnail } from './video-thumbnail';

export interface HistoryScreenItem {
  id: string;
  resultUrl?: string | null;
  thumbnailUrl?: string | null;
  resultType?: 'IMAGE' | 'VIDEO' | 'AUDIO' | null;
  status?: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | string | null;
  prompt?: string | null;
  resultWidth?: number | null;
  resultHeight?: number | null;
  feedStatus?: 'APPROVED' | 'PENDING_MODERATION' | string | null;
}

interface HistoryScreenProps {
  items?: HistoryScreenItem[];
  isLoading?: boolean;
  hasError?: boolean;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onCreateFirst?: () => void;
  onRetry: () => void;
  onLoadMore: () => void;
}

const SKELETON_LEFT = [200, 160, 230, 180];
const SKELETON_RIGHT = [160, 230, 180, 200];

export function HistoryScreen({
  items,
  isLoading = false,
  hasError = false,
  hasNextPage = false,
  isFetchingNextPage = false,
  onCreateFirst,
  onRetry,
  onLoadMore,
}: HistoryScreenProps) {
  if (hasError && !isLoading) {
    return (
      <div className="p-4">
        <h1 className="mb-4 text-xl font-bold">История</h1>
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <XCircle className="h-12 w-12 text-red-500" />
          <p className="mt-4 text-muted-foreground">Ошибка загрузки истории</p>
          <button
            onClick={onRetry}
            className="mt-4 flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
          >
            <RefreshCw className="h-4 w-4" />
            Попробовать снова
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <h1 className="mb-4 text-xl font-bold">История</h1>

      {isLoading ? (
        <MasonrySkeleton />
      ) : items && items.length > 0 ? (
        <>
          <HistoryMasonry items={items} />
          <InfiniteScrollTrigger
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            onLoadMore={onLoadMore}
          />
        </>
      ) : (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <p className="text-muted-foreground">У вас пока нет генераций</p>
          <button
            type="button"
            onClick={onCreateFirst}
            className="mt-4 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
          >
            Создать первую
          </button>
        </div>
      )}
    </div>
  );
}

function MasonrySkeleton() {
  return (
    <div className="flex gap-1">
      <div className="flex flex-1 flex-col gap-1">
        {SKELETON_LEFT.map((height, index) => (
          <div key={index} className="animate-pulse rounded-sm bg-card" style={{ height }} />
        ))}
      </div>
      <div className="flex flex-1 flex-col gap-1">
        {SKELETON_RIGHT.map((height, index) => (
          <div key={index} className="animate-pulse rounded-sm bg-card" style={{ height }} />
        ))}
      </div>
    </div>
  );
}

function HistoryMasonry({ items }: { items: HistoryScreenItem[] }) {
  const [left, right] = balanceMasonryColumns(items);
  const basePath = useBasePath();

  return (
    <div className="flex gap-1">
      <div className="flex flex-1 flex-col gap-1">
        {left.map((item) => renderHistoryItem(item, basePath))}
      </div>
      <div className="flex flex-1 flex-col gap-1">
        {right.map((item) => renderHistoryItem(item, basePath))}
      </div>
    </div>
  );
}

function renderHistoryItem(item: HistoryScreenItem, basePath: string) {
  const isCompleted = item.resultUrl && item.status === 'COMPLETED';
  const cardStyle =
    isCompleted && item.resultType === 'IMAGE'
      ? undefined
      : item.resultWidth && item.resultHeight
        ? { aspectRatio: `${item.resultWidth}/${item.resultHeight}` }
        : { minHeight: 120 };

  return (
    <Link
      key={item.id}
      href={`${basePath}/history/${item.id}`}
      className="relative block overflow-hidden bg-card"
      style={cardStyle}
    >
      {isCompleted ? (
        item.resultType === 'VIDEO' ? (
          <VideoThumbnail
            src={item.resultUrl!}
            poster={item.thumbnailUrl}
            alt={item.prompt || 'Video'}
            iconSize="lg"
          />
        ) : (
          <S3Image
            src={item.thumbnailUrl || item.resultUrl!}
            alt={item.prompt || 'Generated'}
            width={item.resultWidth || 390}
            height={item.resultHeight || 390}
            className="block h-auto w-full"
          />
        )
      ) : item.status === 'FAILED' ? (
        <div className="relative aspect-square w-full bg-[#4e4e50]">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/assets/error-icon.svg"
            alt=""
            className="absolute left-1/2 top-[13%] w-[35%] -translate-x-1/2"
          />
          <p className="absolute bottom-[16%] left-0 right-0 px-3 text-center font-sans text-xs leading-[1.75] tracking-tight text-white">
            Что-то пошло не так
          </p>
          <p className="absolute bottom-[6%] left-0 right-0 text-center font-sans text-xs font-semibold text-[#ef6444]">
            Повторить
          </p>
        </div>
      ) : (
        <div className="relative aspect-square w-full bg-[#25272a]">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/assets/logo-full-gray.svg"
            alt=""
            className="absolute left-1/2 top-[22%] w-[36%] -translate-x-1/2"
          />
          <div className="absolute left-1/2 top-[76%] flex -translate-x-1/2 gap-[7px]">
            <span
              className="h-[7px] w-[7px] rounded-full bg-accent animate-bounce-dot"
              style={{ animationDelay: '0ms' }}
            />
            <span
              className="h-[7px] w-[7px] rounded-full bg-accent animate-bounce-dot"
              style={{ animationDelay: '150ms' }}
            />
            <span
              className="h-[7px] w-[7px] rounded-full bg-accent animate-bounce-dot"
              style={{ animationDelay: '300ms' }}
            />
          </div>
        </div>
      )}
      {item.feedStatus === 'APPROVED' && (
        <span
          className="absolute right-0 top-0 flex items-center justify-center rounded-bl-[10px] bg-[#9dd94a]"
          style={{ padding: '2px 2px 4px 5px', height: 21 }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 21 21"
            fill="none"
            className="shrink-0"
            style={{ width: 14, height: 14 }}
          >
            <circle cx="10.5" cy="10.5" r="9" stroke="#fff" strokeWidth="1.4" />
            <path
              d="M6.44 10.46l2.72 2.72 5.44-5.44"
              stroke="#fff"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      )}
      {item.feedStatus === 'PENDING_MODERATION' && (
        <span className="absolute right-0 top-0 z-10 rounded-bl-[10px] bg-[#25272a] px-2 pb-1 pt-0.5 text-[11px] font-medium text-white">
          На модерации
        </span>
      )}
    </Link>
  );
}

function balanceMasonryColumns<
  T extends { resultWidth?: number | null; resultHeight?: number | null },
>(items: T[]): [T[], T[]] {
  const left: T[] = [];
  const right: T[] = [];
  let leftHeight = 0;
  let rightHeight = 0;

  for (const item of items) {
    const ratio =
      item.resultWidth && item.resultHeight ? item.resultHeight / item.resultWidth : 1;

    if (leftHeight <= rightHeight) {
      left.push(item);
      leftHeight += ratio;
    } else {
      right.push(item);
      rightHeight += ratio;
    }
  }

  return [left, right];
}
